import { useAxiosStore } from '@risksmart-app/components/src/hooks/useAxios';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { getEnv } from '@risksmart-app/components/src/utils/environment';

import { useBeginConnectSlackTRPC } from '@/hooks/mutations/notifications/useBeginConnectSlackTRPC';
import { useConnectSlackTRPC } from '@/hooks/mutations/notifications/useConnectSlackTRPC';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { handleError } from '@/utils/errorUtils';

const scopes = ['chat:write'];
const redirectUri =
  window.location.hostname === 'localhost'
    ? 'https://redirectmeto.com/http://localhost:3000/slack-callback' // hacky workaround for slack redirect on localhost
    : window.location.origin + '/slack-callback';
const NONCE_STORAGE_KEY = 'slack-oauth-nonce';

/*
 * useSlack hook provides functions to connect/disconnect Slack from Knock.
 */
export const useSlack = () => {
  const { authorisedAxiosInstance } = useAxiosStore();
  const { addNotification } = useNotifications();
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const { connectSlack } = useConnectSlackTRPC();
  const { beginConnectSlack } = useBeginConnectSlackTRPC();

  /*
   * Ask the server for the OAuth `state` (COI-709). It used to be generated
   * here, which meant only this browser could vouch for it; the server now mints
   * a token bound to the signed-in user and verifies it on the way back, so a
   * callback the user did not start is rejected.
   */
  const mintState = async (): Promise<string> => {
    if (trpcEnabled) {
      return beginConnectSlack();
    }

    const response = await authorisedAxiosInstance.post<{ state: string }>(
      '/slack/begin-connect'
    );

    return response.data.state;
  };

  /*
   * This function opens a new window to Slack's OAuth flow.
   */
  const loginWithSlack = async () => {
    const slackClientId: string | undefined = getEnv(
      'REACT_APP_SLACK_CLIENT_ID',
      true
    );

    if (!slackClientId) {
      addNotification({
        content: 'Failed to launch slack login. Client ID not set',
        type: 'error',
      });

      return Promise.reject();
    }

    // Minted before the popup opens: a state the server will not accept makes the
    // whole flow pointless, so fail here rather than after the user has granted
    // Slack access.
    let state: string;
    try {
      state = await mintState();
    } catch (error) {
      handleError(error);
      addNotification({
        content: 'Failed to launch slack login. Please try again',
        type: 'error',
      });

      return Promise.reject(error);
    }

    // Still stored for the browser-side comparison in `exchangeSlackCode` — now
    // defence in depth rather than the only check. The popup inherits a copy of
    // sessionStorage from this window, which is why the write must precede
    // `window.open`.
    sessionStorage.setItem(NONCE_STORAGE_KEY, state);

    const openedWindow = window.open(
      `https://slack.com/oauth/v2/authorize?scope=${scopes.join(
        ','
      )}&client_id=${slackClientId}&redirect_uri=${redirectUri}&state=${state}`
    );

    return new Promise<void>((resolve) => {
      const interval = setInterval(() => {
        if (openedWindow?.closed) {
          clearInterval(interval);
          resolve();
        }
      }, 500);
    });
  };

  /*
   * This function exchanges the code provided by Slack and connects Slack to Knock.
   *
   * The browser-side nonce comparison below is now the FIRST of two checks: since
   * COI-709 both the legacy `POST /slack/callback` handler and its V3 replacement
   * (`frontend.notifications.slack.connect`) verify the state server-side against
   * the signed-in user. This one stays because it fails without a round trip.
   */
  const exchangeSlackCode = async (code: string, state: string) => {
    const storedState = sessionStorage.getItem(NONCE_STORAGE_KEY);
    sessionStorage.removeItem(NONCE_STORAGE_KEY);
    if (state !== storedState) {
      throw new Error('Invalid state');
    }

    try {
      if (trpcEnabled) {
        await connectSlack(code, state);
      } else {
        await authorisedAxiosInstance.post('/slack/callback', { code, state });
      }

      return true;
    } catch (error) {
      // Report before swallowing. The boolean contract is deliberate — the
      // callback page renders its own failure state and must not toast — but a
      // silently-dropped exchange failure is undiagnosable, and this is the only
      // catch in the flow.
      handleError(error);

      return false;
    }
  };

  return { loginWithSlack, exchangeSlackCode };
};
